import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '../../../components/organisms/Table';
import { TableCellModifier } from '../../../components/organisms/Table/TableCell';
import { isEmptyArray } from '../../../lib/typeGuards';
import { Statistics, StatisticTypes } from '../ContentSearchStatistics.types';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ContentLink } from '../../../_lib/components/ContentLink';
import { Strong } from '../../../components/atoms/Strong/Strong';
import ModalDialogAdditionalScrollContainer from '../../../components/organisms/ModalDialog/ModalDialogAdditionalScrollContainer';

export type ContentSearchStatisticsTableProps = {
  statistics: Statistics[];
  type: StatisticTypes;
};

export function ContentSearchStatisticsTable({ statistics, type }: ContentSearchStatisticsTableProps) {
  const { t } = useSonTranslation('ContentSearchStatistics');

  const searchQuery = t('statistics.searchQuery');
  const totalSearchQueriesCount = t('statistics.totalSearchQueriesCount');
  const instantSearchQueriesPercentage = t('statistics.instantSearchQueriesPercentage');
  const filteredSearchQueriesPercentage = t('statistics.filteredSearchQueriesPercentage');
  const searchResultsCount = t('statistics.searchResultsCount');

  if (isEmptyArray(statistics)) {
    return null;
  }

  function getCaption() {
    return (
      <>
        <Strong>{t(`lastContentSearches.${type}`)}</Strong>
        {` ${t('lastContentSearches.withoutToday')}`}
      </>
    );
  }

  function replaceDotToComma(searchCount: number): string {
    const searchCountString = searchCount.toString();
    if (searchCountString.includes('.')) {
      return searchCountString.replace('.', ',');
    }
    return searchCountString;
  }

  return (
    <ModalDialogAdditionalScrollContainer>
      <div className="content-search-statistics">
        <Table caption={getCaption()} captionHidden={false}>
          <TableHead>
            <TableRow>
              <TableCell modifier={[TableCellModifier.LOWPADDING, TableCellModifier.WIDTH_170]}>
                {searchQuery}
              </TableCell>
              <TableCell
                modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING, TableCellModifier.WIDTH_150]}
              >
                {totalSearchQueriesCount}
              </TableCell>
              <TableCell modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING]}>
                {instantSearchQueriesPercentage}
              </TableCell>
              <TableCell
                modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING, TableCellModifier.WIDTH_150]}
              >
                {filteredSearchQueriesPercentage}
              </TableCell>
              <TableCell
                modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING, TableCellModifier.WIDTH_190]}
              >
                {searchResultsCount}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statistics?.map(item => (
              <TableRow key={item.searchQuery + type}>
                <TableCell modifier={[TableCellModifier.LOWPADDING]} dataHead={searchQuery}>
                  <ContentLink to={item.url.url}>{item.searchQuery}</ContentLink>
                </TableCell>
                <TableCell
                  modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING]}
                  dataHead={totalSearchQueriesCount}
                >
                  {replaceDotToComma(item.totalSearchQueriesCount)}
                </TableCell>
                <TableCell
                  modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING]}
                  dataHead={instantSearchQueriesPercentage}
                >
                  {t('searchQueriesPercentage', { percentage: replaceDotToComma(item.instantSearchQueriesPercentage) })}
                </TableCell>
                <TableCell
                  modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING]}
                  dataHead={filteredSearchQueriesPercentage}
                >
                  {t('searchQueriesPercentage', {
                    percentage: replaceDotToComma(item.filteredSearchQueriesPercentage),
                  })}
                </TableCell>
                <TableCell
                  modifier={[TableCellModifier.ALIGN_RIGHT, TableCellModifier.LOWPADDING]}
                  dataHead={searchResultsCount}
                >
                  {t('searchResultsCountAvarage', {
                    mean: replaceDotToComma(item.searchResultsCount.mean),
                    min: replaceDotToComma(item.searchResultsCount.min),
                    max: replaceDotToComma(item.searchResultsCount.max),
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </ModalDialogAdditionalScrollContainer>
  );
}
